// CORE
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
// MUI
import QButton from 'components/QButton';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// APP-COMPONENTS
import { authSelectors } from 'app-components/flux/auth';
import { actionHelpers } from 'app-components/flux/core';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { rootUiActions } from 'app-components/flux/root-ui';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { getEnvironmentConfig, getLogger, tracker } from 'app-components/utils/core';

import SetupPage from 'components/Dialogs/IdentityVerification/SetupPage';
// Shared
import StdDialog from 'components/Dialogs/StdDialog';
import LoadingView from 'components/LoadingView';
import ZeroStateView from 'components/ZeroStateView';

import QIllustration from 'components/QIllustration';
// LOCAL
import CreateAndVerifyPhoneForIdentityVerification from './CreateAndVerifyPhone';
import VerifyPhoneForIdentityVerification from './VerifyPhone';
import VerifyPhoneSentForIdentityVerification from './VerifyPhoneSent';
import { phoneDialogIndexEnums } from './phoneDialogIndexEnums';
import useStyles from './styles';

const logger = getLogger('components/Dialogs/IdentityVerification');

export const DIALOG_TYPE_PHONE_VERIFICATION_DIALOG = 'DIALOG_TYPE_PHONE_VERIFICATION_DIALOG';

/*  VIEW INDEXES MAP
  READY : For when selector data is ready to display
  FAILED : For when there was a failure retrieving selector data
  LOADING : For when selector data is loading
*/
function PhoneVerificationDialog(props) {
  const isMounted = useRef();
  const theme = useTheme();

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { initiator, onConfirm, startTab } = props;

  // =============== SELECTORS =================
  const profile = useSelector(profileSelectors.getProfile);
  const authSession = useSelector(authSelectors.getAuthSession);
  const phoneVerified = useSelector(profileSelectors.getPhoneVerified);

  // ================= VARIABLES ====================
  const doesPhoneExist = profile?.get('primaryPhone');
  const phoneNumber = profile?.primaryPhone?.number;

  // ==================== STATES ===================
  const [tabIndex, setTabIndex] = useState(startTab || phoneDialogIndexEnums.OPENING);
  const [showCheckAgainButton, setShowCheckAgainButton] = useState(false);
  const [isSendingText, setIsSendingText] = useState(false);

  //  ================= VIEW STATE LOGIC =====================
  const profileView = useLoadState(profileSelectors);

  const mainView = useMemo(() => {
    const showSkeletonView = loadState.skeletonStates.includes(profileView) || tabIndex === -1;
    const showTryAgainView =
      (loadState.tryAgainStates.includes(profileView) || loadState.updateRequiredStates.includes(profileView)) &&
      tabIndex !== -1;
    const showNoDataView = loadState.noDataStates.includes(profileView) && tabIndex !== -1;
    const showReadyView = loadState.readyStates.includes(profileView) && tabIndex !== -1;

    //  Ordered by priority
    if (showReadyView || showNoDataView) {
      //  There will be no EMPTY view for this dialog
      return 'READY';
    } else if (showTryAgainView) {
      return 'FAILED';
    } else if (showSkeletonView) {
      return 'LOADING';
    }
  }, [profileView, tabIndex]);

  // ==================== CALLBACK FUNCTIONS =====================
  const setPreviousIndex = () => {
    if (tabIndex === phoneDialogIndexEnums.PENDING) {
      setTabIndex(phoneDialogIndexEnums.SUBMIT);
    } else if (tabIndex === phoneDialogIndexEnums.SUBMIT) {
      setTabIndex(phoneDialogIndexEnums.OPENING);
    }
  };

  const refreshData = useCallback(() => {
    dispatch(profileActions.getProfile());
  }, [dispatch]);

  const handleClose = useCallback(
    (_event) => {
      if (phoneVerified && isMounted.current === true) {
        tracker.track(tracker.events.phoneVerified);
      }
      dispatch(rootUiActions.removeDialog());
    },
    [dispatch, phoneVerified],
  );

  const handlePhoneCheck = useCallback(async () => {
    dispatch(profileActions.getProfile());
  }, [dispatch]);

  const openMyAccount = useCallback(() => {
    window.open(`${getEnvironmentConfig().myaccount_url}/security?hide-nav&bearer=${authSession.accessToken}`);
    dispatch(profileActions.createPhone());
  }, [authSession.accessToken, dispatch]);

  const handleSendPhone = useCallback(
    async (isResent) => {
      actionHelpers
        .bindPromiseAction(dispatch, profileActions.getProfile)()
        .then((_resp) => {
          if (isMounted.current === true) {
            if (phoneVerified) return;
            setTabIndex(phoneDialogIndexEnums.PENDING);
            setIsSendingText(false);

            // Due to the constant polling in the saga, the action is not "finished" for a long time, so call mixpanel events now
            if (isResent) {
              tracker.track(tracker.events.verificationPhoneResent);
            } else {
              tracker.track(tracker.events.verificationPhoneSent);
            }

            actionHelpers
              .bindPromiseAction(dispatch, profileActions.verifyPhone)()
              .then((_resp) => {
                if (isMounted.current === true) {
                  handlePhoneCheck();
                  setShowCheckAgainButton(true);
                }
                logger.log('Successfully sent text');
              })
              .catch((_error) => {
                if (isMounted.current === true) {
                  handlePhoneCheck();
                  setShowCheckAgainButton(true);
                }
              });
          }
        })
        .catch((error) => assert(false, error));
    },
    [dispatch, handlePhoneCheck, phoneVerified],
  );

  const resendPhone = useCallback(() => {
    setIsSendingText(true);
    setTabIndex(phoneDialogIndexEnums.LOADING);
    setTimeout(() => {
      handleSendPhone(true);
    }, 2500);
  }, [handleSendPhone]);

  const handleContinue = useCallback(
    (e) => {
      if (tabIndex === phoneDialogIndexEnums.OPENING) {
        //  tracker.track(tracker.events.viewPhoneVerification);
        setTabIndex(phoneDialogIndexEnums.SUBMIT);
      } else if (tabIndex === phoneDialogIndexEnums.SUBMIT && doesPhoneExist) {
        handleSendPhone();
      } else if (tabIndex === phoneDialogIndexEnums.SUBMIT && !doesPhoneExist) {
        openMyAccount();
      } else if (phoneVerified) {
        handleClose(e, 'COMPLETE');
        onConfirm();
      }
    },
    [doesPhoneExist, handleClose, handleSendPhone, onConfirm, openMyAccount, tabIndex, phoneVerified],
  );

  // =================== EFFECTS ===================

  // track mounted state because of async promise
  useEffect(() => {
    dispatch(profileActions.getProfile());
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, [dispatch]);

  useEffect(() => {
    // Check if the phone has been verified!
    if (phoneVerified) {
      setTimeout(() => handleContinue(), 3000);
      setTabIndex(phoneDialogIndexEnums.SUCCESS);
    }
  }, [handleContinue, phoneVerified]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (tabIndex === phoneDialogIndexEnums.OPENING) {
      tracker.track(tracker.events.viewTransfersIntroScreen, { initiator: 'phone' });
    } else if (tabIndex === phoneDialogIndexEnums.SUBMIT) {
      tracker.track(tracker.events.viewPhoneVerification);
    }
  }, [initiator, tabIndex]);

  // ================ DERIVED VALUES ====================

  let dialogTitle = 'Phone verification';
  dialogTitle = tabIndex === phoneDialogIndexEnums.OPENING ? 'Set up account transfers' : dialogTitle;

  if (tabIndex === phoneDialogIndexEnums.SUBMIT && !phoneVerified && doesPhoneExist) {
    dialogTitle = 'Verify your phone number';
  } else if (tabIndex === phoneDialogIndexEnums.SUBMIT && !phoneVerified && !doesPhoneExist) {
    dialogTitle = 'Add your phone number';
  }
  dialogTitle = tabIndex === phoneDialogIndexEnums.PENDING && !phoneVerified ? 'Verification text sent' : dialogTitle;
  dialogTitle = tabIndex === phoneDialogIndexEnums.SUCCESS ? 'Phone number verified' : dialogTitle;

  let continueText = 'Continue';
  continueText = tabIndex === phoneDialogIndexEnums.OPENING ? 'Get started' : continueText;
  continueText =
    tabIndex === phoneDialogIndexEnums.SUBMIT && !phoneVerified && doesPhoneExist ? 'Send text' : continueText;
  continueText =
    tabIndex === phoneDialogIndexEnums.SUBMIT && !phoneVerified && !doesPhoneExist ? 'Add phone number' : continueText;

  return (
    <StdDialog
      onBack={setPreviousIndex}
      showBackButton={
        mainView !== 'LOADING' &&
        (tabIndex === phoneDialogIndexEnums.PENDING || tabIndex === phoneDialogIndexEnums.SUBMIT)
      }
      disableBackdropClick
      onClose={handleClose}
      open
      sharedcomponentid={'PHONE_VERIFICATION_DIALOG'}
      showCloseButton={tabIndex !== phoneDialogIndexEnums.SUCCESS}
      size={'md'}
      title={dialogTitle}
    >
      <DialogContent>
        {tabIndex === phoneDialogIndexEnums.OPENING && mainView === 'READY' && <SetupPage />}

        {tabIndex === phoneDialogIndexEnums.SUBMIT && mainView === 'READY' && !phoneVerified && doesPhoneExist && (
          <VerifyPhoneForIdentityVerification phoneNumber={phoneNumber} />
        )}

        {tabIndex === phoneDialogIndexEnums.SUBMIT && mainView === 'READY' && !phoneVerified && !doesPhoneExist && (
          <CreateAndVerifyPhoneForIdentityVerification phoneNumber={phoneNumber} />
        )}

        {tabIndex === phoneDialogIndexEnums.PENDING && mainView === 'READY' && !phoneVerified && (
          <VerifyPhoneSentForIdentityVerification phoneNumber={phoneNumber} sendVerifyText={resendPhone} />
        )}

        {tabIndex === phoneDialogIndexEnums.SUCCESS && mainView === 'READY' && phoneVerified && (
          <Box
            textAlign='center'
            pl={18}
            pr={18}
            pt={8}
            pb={8}
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
          >
            <QIllustration name='thumbsUp' size='lg' />
            <br />
            <Typography variant='headlineRegular'>Phone number verified!</Typography>
          </Box>
        )}
        {(mainView === 'LOADING' || tabIndex === phoneDialogIndexEnums.LOADING) && ( // Override mainView if tabIndex is LOADING
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            <LoadingView size={100} />
            {isSendingText && (
              <Typography fontWeight={500} variant='titleMedium'>
                Sending text...
              </Typography>
            )}
          </Box>
        )}
        {mainView === 'FAILED' && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              button={
                <QButton id={'try-again-button'} variant='outlined' onClick={refreshData}>
                  TRY AGAIN
                </QButton>
              }
              id='failed-to-fetch'
              title='Unable to fetch data'
              description="We can't fetch your data from the server. Please try again"
              qIllustration='planeCrash'
            />
          </Box>
        )}
      </DialogContent>
      {mainView === 'READY' && tabIndex !== phoneDialogIndexEnums.SUCCESS && (
        <DialogActions>
          {tabIndex === phoneDialogIndexEnums.PENDING &&
            !phoneVerified &&
            (showCheckAgainButton ? (
              <QButton
                variant='outlined'
                id='id-verification-phone-check'
                aria-label='phone-check'
                onClick={handlePhoneCheck}
              >
                {'check again'}
              </QButton>
            ) : (
              <QButton
                width={100}
                disabled
                sx={{
                  '&.Mui-disabled': {
                    opacity: 1,
                    backgroundColor: `${theme.applyOpacityToHex?.(theme.palette.primary.main, 0.1)} !important`,
                  },
                }}
                variant='contained'
              >
                <CircularProgress size={20} className={classes.spinner} thickness={5} />
              </QButton>
            ))}
          {mainView === 'READY' &&
            !(tabIndex === phoneDialogIndexEnums.PENDING) &&
            !phoneVerified &&
            tabIndex !== phoneDialogIndexEnums.LOADING && ( // Override mainView if tabIndex is LOADING
              <QButton variant='contained' id='id-verification-continue' aria-label='continue' onClick={handleContinue}>
                {continueText}
              </QButton>
            )}
        </DialogActions>
      )}
    </StdDialog>
  );
}

PhoneVerificationDialog.propTypes = {
  initiator: PropTypes.string,
  onConfirm: PropTypes.func,
  startTab: PropTypes.number,
};

export default PhoneVerificationDialog;
